import { z } from "zod"
import type { CATSegmentQEResponse } from "./translation.types"

const segmentSchema = z.object({
  segmentId: z.string(),
  qeIndex: z.number().int(),
  score: z.number().finite(),
})

const qeResponseSchema = z.discriminatedUnion("availability", [
  z.object({
    success: z.literal(true),
    availability: z.literal("available"),
    systemScore: z.number().finite(),
    segments: z.array(segmentSchema),
    reason: z.null().default(null),
  }),
  z.object({
    success: z.literal(true),
    availability: z.literal("unavailable"),
    systemScore: z.null(),
    segments: z.array(segmentSchema).length(0),
    reason: z.enum([
      "projection_state_missing",
      "projection_disabled",
      "system_score_missing",
    ]),
  }),
])

export class CATQEInvalidResponseError extends Error {
  constructor() {
    super("Invalid CAT QE response")
    this.name = "CATQEInvalidResponseError"
  }
}

export const normalizeCatSegmentQE = (data: unknown): CATSegmentQEResponse => {
  // Older APIs return only success, systemScore and segments for an available result.
  const candidate =
    data && typeof data === "object" && !("availability" in data)
      ? { ...data, availability: "available", reason: null }
      : data
  const parsed = qeResponseSchema.safeParse(candidate)
  if (!parsed.success) throw new CATQEInvalidResponseError()
  return parsed.data
}

export const isCatQERetryable = (error: unknown) => {
  if (error instanceof CATQEInvalidResponseError) return false
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status
  return status == null || status === 408 || status === 429 || status >= 500
}

export const isCatQEProjectionPending = (
  result: CATSegmentQEResponse | undefined
) =>
  result?.availability === "unavailable" &&
  result.reason !== "projection_disabled"

export const catQEStaleTime = (result: CATSegmentQEResponse | undefined) =>
  isCatQEProjectionPending(result) ? 15_000 : Infinity
